import { createReport } from '@/services/reports/util';

/**
 * Parameter shape for the fresh-straw summary export. Matches the
 * `ReportSpec.Parameters` dictionary the `FreshStrawSummaryCsvGenerator`
 * in the report service reads via `GetDateTimeParameter` / `GetGuidParameter`.
 *
 * `fertilizationDate` is required; the other filters are optional and mirror
 * the session's filter panel state. Missing values are simply dropped by
 * the server-side parser.
 */
export interface FreshStrawSummaryReportParams {
  fertilizationDate: string;
  fertilizationSite?: string;
  accountId?: string;
  freshShipmentId?: string;
}

/**
 * Hook factory for the fresh-straw transfer session summary CSV export.
 * Matches the `ReportType = "FreshStrawSummary"` registered with the report
 * service's `IReportGenerator` dispatcher.
 *
 * Usage:
 *
 *     const [generate, { isLoading }] = useFreshStrawSummaryReport();
 *     await generate({ fertilizationDate, accountId });
 *
 * The hook takes care of the POST, the success toast, and opening the
 * resulting blob URL in a new tab. Errors surface as toasts too.
 */
export const useFreshStrawSummaryReport =
  createReport<FreshStrawSummaryReportParams>('FreshStrawSummary');

/** Parameter shape for the fresh-straw embryo detail export. Mirrors the
 *  summary filters 1-for-1 so the same session state can drive both. */
export type FreshStrawDetailReportParams = FreshStrawSummaryReportParams;

/**
 * Hook factory for the fresh-straw embryo detail CSV export. Unlike the
 * summary report (one row per fertilization) this returns one row per
 * embryo so auditors can see individual straws, recipients, and state.
 */
export const useFreshStrawDetailReport =
  createReport<FreshStrawDetailReportParams>('FreshStrawDetail');

/**
 * Parameter shape for the DairyComp transfer CSV export. Calls the legacy
 * `TOA_GetFreshStrawTransferCSV` stored proc via the report service and
 * writes the 17-column DairyComp-compatible layout.
 */
export interface FreshStrawDairyCompReportParams {
  transferDate: string;
  transferSiteId: string;
  fertilizationSiteId: string;
  accountId?: string;
  freshShipmentId?: string;
  farmLocationFilter?: string;
}

export const useFreshStrawDairyCompReport =
  createReport<FreshStrawDairyCompReportParams>('FreshStrawDairyComp');
